// @flow
import { has } from "../../utils/helpers/objects";

import ActionCollection from "../actions/ActionCollection";
import DetailModel from "../detail/DetailModel";
import ContentConfiguration from "../contentconfiguration/ContentConfiguration";
import AttributeSetModel from "../attributes/AttributeSetModel";

import createAttribute from "../attributes/_createAttribute";
import CompositeAttributeModel from "../attributes/CompositeAttributeModel";

import type { ModularUIModel } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkCollection from "../links/LinkCollection";
import type ListItemModel from "../list/ListItemModel";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Detail of a list item
 */
export default class ListDetailModel extends DetailModel {
  _contentConfiguration: ContentConfiguration;
  _listitem: ListItemModel;
  _givenAnswers: ?CompositeAttributeModel;
  _results: ?CompositeAttributeModel;
  _eventdata: Array<AttributeSetModel>;

  /**
   * Construct
   */
  constructor(modularUIResponse: ModularUIResponse) {
    super(modularUIResponse);

    this._actionCollection = new ActionCollection();

    this._contentConfiguration = new ContentConfiguration(
      this.contributions ? this.contributions.content : {},
    );

    this.setResultSection();
    this.setEventData();
  }

  /**
   * Add links to expand on initialization of this model
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const listitemPanels = this.listitem
      ? this.listitem.links.getLinksByGroup("panel").all
      : [];

    const links = [...super.getInitialChildModelLinks(), ...listitemPanels];

    if (this.hasResults) {
      if (this.results) {
        links.push(...this.results.getInitialChildModelLinks());
      }
      if (this.givenAnswers) {
        links.push(...this.givenAnswers.getInitialChildModelLinks());
      }
    }

    return links;
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this._attributeCollection.setChildModels(models, errors);

    if (this.results) {
      this.results.setChildModels(models, errors);
    }

    if (this.givenAnswers) {
      this.givenAnswers.setChildModels(models, errors);
    }
  }

  /**
   */
  get type(): string {
    return "ListDetail";
  }

  /**
   */
  static get modelName(): string {
    return "ListDetailModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    let resourcetype = data.contributions?.resourcetype ?? "unknown";
    resourcetype = resourcetype.toLowerCase();

    return (
      resourcetype.endsWith("listdetail") ||
      resourcetype.endsWith("listpaneldetail") ||
      resourcetype.endsWith("datastorepaneldetail")
    );
  }

  /**
   * Set listitem of this listdetail and transfer listitem actions to listdetail actions
   */
  set listitem(listitem: ListItemModel) {
    this._listitem = listitem;

    // transfer listitem actions to _actions
    this._actionCollection = listitem.actionCollection;
  }

  /**
   * Get listitem
   */
  get listitem(): ListItemModel {
    return this._listitem;
  }

  /**
   * Getting panel links
   */
  get panelLinks(): LinkCollection {
    return this.listitem.links.getLinksByGroup("panel");
  }

  /**
   * Getting the contentConfiguration
   */
  get contentConfiguration(): ContentConfiguration {
    return this._contentConfiguration;
  }

  /**
   */
  set contentConfiguration(configuration: ContentConfiguration) {
    this._contentConfiguration = configuration;
  }

  /**
   * Getting the introduction text configured on the case view
   */
  get introtext(): string {
    if (this.data._content) {
      return this.data._content.text.message;
    }

    if (this.contributions.text) {
      return this.contributions.text.message ?? this.contributions.text;
    }

    if (Array.isArray(this.contributions.texts)) {
      return this.contributions.texts[0].text;
    }

    return "";
  }

  /**
   */
  setResultSection() {
    if (
      has(this.contributions, "resultSection") &&
      has(this.data, "resultSection")
    ) {
      this.setResults();
      this.setGivenAnswers();
    }
  }

  /**
   */
  setResults() {
    if (
      has(this.data, "resultSection.results") &&
      has(this.contributions, "resultSection.results")
    ) {
      const resultsData = this.data.resultSection.results;
      const resultsContributions = this.contributions.resultSection.results;
      if (resultsContributions.type === "composite") {
        resultsContributions.children = resultsContributions.children.filter(
          (child) => {
            const childKey = Object.keys(child)[0];
            return childKey in resultsData;
          },
        );
      }

      const results = createAttribute(
        "results",
        {
          key: "results",
          dynamicschema: this.data.dynamicschema,
          content: this.data.content?.results,
          ...this.data.resultSection,
        },
        resultsContributions,
      );

      if (results instanceof CompositeAttributeModel) {
        results.isResult = true;
        results.indicateContentConfiguration(this.contentConfiguration);
        this._results = results;
      }
    }
  }

  /**
   */
  setGivenAnswers() {
    if (
      has(this.data, "resultSection.givenAnswers") &&
      has(this.contributions, "resultSection.givenAnswers")
    ) {
      const givenAnswersData = this.data.resultSection.givenAnswers;
      const givenAnswersContributions =
        this.contributions.resultSection.givenAnswers;

      const filteredContributions = givenAnswersContributions.children
        ? {
            ...givenAnswersContributions,
            children: givenAnswersContributions.children.filter((child) => {
              const childKey = Object.keys(child)[0];
              return childKey in givenAnswersData;
            }),
          }
        : givenAnswersContributions;

      const givenAnswers = createAttribute(
        "givenAnswers",
        {
          key: "givenAnswers",
          dynamicschema: this.data.dynamicschema,
          content: this.data.content?.givenAnswers,
          ...this.data.resultSection,
        },
        filteredContributions,
      );

      if (givenAnswers instanceof CompositeAttributeModel) {
        givenAnswers.isResult = true;
        this._givenAnswers = givenAnswers;
      }
    }
  }

  /**
   */
  get givenAnswers(): ?CompositeAttributeModel {
    return this._givenAnswers;
  }

  /**
   */
  get hasResults(): boolean {
    return has(this.contributions, "resultSection");
  }

  /**
   */
  get results(): ?CompositeAttributeModel {
    return this._results;
  }

  /**
   */
  get hasEventData(): boolean {
    return (
      has(this.contributions, "eventdata") &&
      has(this.data, "eventdata") &&
      this.data.eventdata != null &&
      this.contributions.eventdata != null
    );
  }

  /**
   */
  addAttributes(key: string, eventData: Object, eventContributions: Object) {
    this._eventdata.push(
      new AttributeSetModel(
        key,
        eventData,
        eventContributions,
        this.modelOptions,
      ),
    );
  }

  /**
   */
  setEventData() {
    if (this.hasEventData) {
      this._eventdata = [];

      this.contributions.eventdata.forEach((eventDataContribution) => {
        const [key] = Object.keys(eventDataContribution);
        if (key in this.data.eventdata) {
          if (Array.isArray(this.data.eventdata[key])) {
            this.data.eventdata[key].forEach((eventDataData, i) => {
              this.addAttributes(
                `${key}-${i + 1}`,
                {
                  ...eventDataData,
                  dynamicschema: this.data.dynamicschema,
                },
                eventDataContribution[key],
              );
            });
          } else {
            this.addAttributes(
              key,
              {
                ...this.data.eventdata[key],
                dynamicschema: this.data.dynamicschema,
              },
              eventDataContribution[key],
            );
          }
        }
      });
    }
  }

  /**
   */
  get eventdata(): Array<AttributeSetModel> {
    return this._eventdata;
  }
}
